/**
 * Pure decision logic for reconciling a live session's Firebase role claim
 * against the database role. No Firebase / store / Sentry access lives here so
 * the full decision matrix stays unit-testable.
 */

// The only value that differs in spelling between the DB `type` field and the
// Firebase `userType` claim: the legacy alias for "creator lite".
const ROLE_ALIASES: Record<string, string> = {
  creatorLite: "creator lite",
};

export const normalizeRole = (role: string | null | undefined): string => {
  if (!role) return "";
  return ROLE_ALIASES[role] ?? role;
};

export const rolesMatch = (
  a: string | null | undefined,
  b: string | null | undefined,
): boolean => normalizeRole(a) === normalizeRole(b);

export const buildRoleSignature = (
  dbRole: string | null | undefined,
  tokenRole: string | null | undefined,
): string => `${normalizeRole(dbRole)}|${normalizeRole(tokenRole)}`;

export type ReconcilePhase = "initial" | "afterRefresh";
export type ReconcileAction = "none" | "refresh" | "report";

/**
 * Decide what the reconcile hook should do next.
 * - "none":    nothing to do (match, admin-core, or an already-tried mismatch).
 * - "refresh": a fresh mismatch — force one token refresh.
 * - "report":  still mismatched after a refresh — Sentry, keep the token role.
 */
export const decideReconcileAction = (params: {
  dbRole: string | null | undefined;
  tokenRole: string | null | undefined;
  isAdminCore: boolean;
  phase: ReconcilePhase;
  lastUnresolvedSignature: string | null;
}): ReconcileAction => {
  const { dbRole, tokenRole, isAdminCore, phase, lastUnresolvedSignature } =
    params;

  // Admin-core users carry an intentional impersonation override in the store,
  // so their token role legitimately differs from their DB role. Never reconcile.
  if (isAdminCore) return "none";

  if (rolesMatch(dbRole, tokenRole)) return "none";

  if (phase === "initial") {
    const signature = buildRoleSignature(dbRole, tokenRole);
    // Anti-storm: don't re-refresh a mismatch we already failed to resolve.
    if (lastUnresolvedSignature === signature) return "none";
    return "refresh";
  }

  // phase === "afterRefresh" and still mismatched.
  return "report";
};
